import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import type { CardProps } from "@mui/material/Card";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import type { BaseMoleculeProps, ComponentColor } from "../../types";
import { usePrice } from "@/hooks";

export interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  modifiers?: string[];
  notes?: string;
}

export interface OrderCardProps extends BaseMoleculeProps {
  /**
   * Order ID
   */
  orderId: string;

  /**
   * Order number for display
   */
  orderNumber?: string;

  /**
   * Order status
   */
  status:
    | "pending"
    | "confirmed"
    | "preparing"
    | "ready"
    | "completed"
    | "cancelled";

  /**
   * Order items
   */
  items: OrderItem[];

  /**
   * Total price
   */
  totalPrice: number;

  /**
   * Order date
   */
  orderDate?: Date;

  /**
   * Customer name or table number
   */
  customerInfo?: string;

  /**
   * Special instructions
   */
  instructions?: string;

  /**
   * Whether to show order details by default
   */
  expanded?: boolean;

  /**
   * Whether to show actions
   */
  showActions?: boolean;

  /**
   * Action handlers
   */
  onViewDetails?: (orderId: string) => void;
  onUpdateStatus?: (orderId: string, status: string) => void;
  onCancel?: (orderId: string) => void;

  /**
   * Custom action buttons
   */
  customActions?: React.ReactNode;

  /**
   * Additional Card props
   */
  cardProps?: Omit<CardProps, "children">;
}

const OrderCardComponent: React.FC<OrderCardProps> = ({
  orderId,
  orderNumber,
  status,
  items,
  totalPrice,
  orderDate,
  customerInfo,
  instructions,
  expanded: defaultExpanded = false,
  showActions = true,
  onViewDetails,
  onUpdateStatus,
  onCancel,
  customActions,
  variant = "default",
  className,
  "data-testid": dataTestId,
  cardProps,
}) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  // Status color mapping
  const getStatusColor = (status: string): ComponentColor => {
    switch (status) {
      case "pending":
        return "warning";
      case "confirmed":
        return "info";
      case "preparing":
        return "primary";
      case "ready":
        return "success";
      case "completed":
        return "success";
      case "cancelled":
        return "error";
      default:
        return "primary";
    }
  };

  // Status text mapping
  const getStatusText = (status: string): string => {
    switch (status) {
      case "pending":
        return "Ожидает";
      case "confirmed":
        return "Подтвержден";
      case "preparing":
        return "Готовится";
      case "ready":
        return "Готов";
      case "completed":
        return "Завершен";
      case "cancelled":
        return "Отменен";
      default:
        return status;
    }
  };

  const price = usePrice();

  // Format date
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Card
      className={className}
      data-testid={dataTestId}
      elevation={variant === "compact" ? 1 : variant === "expanded" ? 4 : 2}
      sx={{
        // Mobile-first responsive styling
        marginBottom: {
          xs: 1, // Mobile
          x2l: 2, // Desktop
        },
        // Variant-specific styling
        ...(variant === "compact" && {
          "& .MuiCardContent-root": {
            padding: 1,
            "&:last-child": {
              paddingBottom: 1,
            },
          },
        }),
        ...(variant === "expanded" && {
          "& .MuiCardContent-root": {
            padding: 3,
            "&:last-child": {
              paddingBottom: 3,
            },
          },
        }),
      }}
      {...cardProps}
    >
      <CardContent>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          marginBottom={2}
          sx={{
            flexDirection: {
              xs: "column", // Mobile - stack vertically
              x2l: "row", // Tablet+ - side by side
            },
            gap: {
              xs: 1, // Mobile spacing
              x2l: 2, // Tablet+ spacing
            },
          }}
        >
          <Box>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontSize: {
                  xs: "1rem", // Mobile
                  x2l: "1.25rem", // Desktop
                },
                fontWeight: 600,
              }}
            >
              {orderNumber
                ? `Заказ #${orderNumber}`
                : `Заказ ${orderId.slice(-6)}`}
            </Typography>

            {customerInfo && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: {
                    xs: "0.75rem", // Mobile
                    x2l: "0.875rem", // Desktop
                  },
                }}
              >
                {customerInfo}
              </Typography>
            )}

            {orderDate && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: {
                    xs: "0.75rem", // Mobile
                    x2l: "0.875rem", // Desktop
                  },
                }}
              >
                {formatDate(orderDate)}
              </Typography>
            )}
          </Box>

          <Chip
            label={getStatusText(status)}
            color={getStatusColor(status)}
            size="small"
            sx={{
              fontSize: {
                xs: "0.75rem", // Mobile
                x2l: "0.875rem", // Desktop
              },
              alignSelf: {
                xs: "flex-start", // Mobile
                x2l: "flex-end", // Tablet+
              },
            }}
          />
        </Box>

        {/* Order Summary */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={1}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: {
                xs: "0.875rem", // Mobile
                x2l: "1rem", // Desktop
              },
            }}
          >
            {items.length} {items.length === 1 ? "позиция" : "позиций"}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontSize: {
                xs: "1rem", // Mobile
                x2l: "1.25rem", // Desktop
              },
              fontWeight: 600,
            }}
          >
            {price.format(totalPrice)}
          </Typography>
        </Box>

        {/* Expand/Collapse Button */}
        <Box display="flex" justifyContent="center" marginBottom={1}>
          <IconButton
            onClick={handleToggleExpanded}
            size="small"
            sx={{
              // Mobile-first responsive touch target
              minHeight: "44px",
              minWidth: "44px",
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Order Details */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ marginBottom: 2 }} />

          <List dense>
            {items.map((item, index) => (
              <ListItem
                key={`${item.id}-${index}`}
                sx={{
                  paddingLeft: 0,
                  paddingRight: 0,
                  // Mobile-first responsive padding
                  paddingTop: {
                    xs: 0.5, // Mobile
                    x2l: 1, // Desktop
                  },
                  paddingBottom: {
                    xs: 0.5, // Mobile
                    x2l: 1, // Desktop
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: {
                            xs: "0.875rem", // Mobile
                            x2l: "1rem", // Desktop
                          },
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Chip
                        label={`×${item.quantity}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: {
                            xs: "0.625rem", // Mobile
                            x2l: "0.75rem", // Desktop
                          },
                          height: "20px",
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      {item.modifiers && item.modifiers.length > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: {
                              xs: "0.75rem", // Mobile
                              x2l: "0.875rem", // Desktop
                            },
                          }}
                        >
                          {item.modifiers.join(", ")}
                        </Typography>
                      )}
                      {item.notes && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: {
                              xs: "0.75rem", // Mobile
                              x2l: "0.875rem", // Desktop
                            },
                            fontStyle: "italic",
                          }}
                        >
                          {item.notes}
                        </Typography>
                      )}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: {
                        xs: "0.875rem", // Mobile
                        x2l: "1rem", // Desktop
                      },
                      fontWeight: 500,
                    }}
                  >
                    {price.format(item.price * item.quantity)}
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {instructions && (
            <>
              <Divider sx={{ marginY: 2 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: {
                    xs: "0.875rem", // Mobile
                    x2l: "1rem", // Desktop
                  },
                  fontStyle: "italic",
                  padding: 1,
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                }}
              >
                <strong>Комментарий:</strong> {instructions}
              </Typography>
            </>
          )}
        </Collapse>
      </CardContent>

      {/* Actions */}
      {showActions &&
        (onViewDetails || onUpdateStatus || onCancel || customActions) && (
          <CardActions
            sx={{
              // Mobile-first responsive actions
              flexDirection: {
                xs: "column", // Mobile - stack vertically
                x2l: "row", // Tablet+ - side by side
              },
              gap: {
                xs: 1, // Mobile spacing
                x2l: 0.5, // Tablet+ spacing
              },
              padding: {
                xs: 2, // Mobile
                x2l: 1, // Desktop
              },
            }}
          >
            {onViewDetails && (
              <Button
                size="small"
                onClick={() => onViewDetails(orderId)}
                sx={{
                  // Mobile-first responsive button
                  minHeight: "44px", // WCAG AA touch target
                  fontSize: {
                    xs: "0.875rem", // Mobile
                    x2l: "1rem", // Desktop
                  },
                  width: {
                    xs: "100%", // Mobile - full width
                    sm: "auto", // Tablet+ - auto width
                  },
                }}
              >
                Подробнее
              </Button>
            )}

            {onUpdateStatus &&
              status !== "completed" &&
              status !== "cancelled" && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => onUpdateStatus(orderId, "completed")}
                  sx={{
                    // Mobile-first responsive button
                    minHeight: "44px", // WCAG AA touch target
                    fontSize: {
                      xs: "0.875rem", // Mobile
                      x2l: "1rem", // Desktop
                    },
                    width: {
                      xs: "100%", // Mobile - full width
                      x2l: "auto", // Tablet+ - auto width
                    },
                  }}
                >
                  Завершить
                </Button>
              )}

            {onCancel && status !== "completed" && status !== "cancelled" && (
              <Button
                size="small"
                color="error"
                onClick={() => onCancel(orderId)}
                sx={{
                  // Mobile-first responsive button
                  minHeight: "44px", // WCAG AA touch target
                  fontSize: {
                    xs: "0.875rem", // Mobile
                    md: "1rem", // Desktop
                  },
                  width: {
                    xs: "100%", // Mobile - full width
                    sm: "auto", // Tablet+ - auto width
                  },
                }}
              >
                Отменить
              </Button>
            )}

            {customActions}
          </CardActions>
        )}
    </Card>
  );
};

// Apply memoization with React.memo
export const OrderCard = React.memo(OrderCardComponent);

// Set display name for debugging
(OrderCard as any).displayName = "OrderCard";
